import { useDispatch, useSelector } from 'react-redux';
import { getUpdated, getIsLoading, getError, getRolloutDaysToPercentageFromAutomation } from '../helpers';
import { RootState } from '../reducers/reducers';
import { AutomationAutoStagedRollout, GoogleAppStoreSettings } from '../types';
import * as React from 'react';
import { AppSettingsUpdatable } from '../actions/updatable';
import { tryUpdateAppSettings, UpdatePlayAppStoreReleaseDefaults } from '../actions/appSettings';
import { AppComponentProps, Nullable } from '../typescriptHelpers';
import { selectAppStoreDefaults, selectAutomationById } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import EditGoogleReleaseSettings from './appSettings/EditGoogleReleaseSettings';

interface EditGoogleAppReleaseDefaultsProps extends AppComponentProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const EditGoogleAppReleaseDefaults: React.FC<EditGoogleAppReleaseDefaultsProps> = (
    props: EditGoogleAppReleaseDefaultsProps,
) => {
    const dispatch = useDispatch();

    const appStoreDefaults = useSelector(
        (state: RootState) => selectAppStoreDefaults(state, props.appId) as GoogleAppStoreSettings,
    );

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const customStagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const releaseSettingsUpdatedSuccess = useSelector((state: RootState) =>
        getUpdated(AppSettingsUpdatable.UpdatePlayAppStoreReleaseDefaults, state),
    );
    const releaseSettingsUpdateLoading = useSelector((state: RootState) =>
        getIsLoading(AppSettingsUpdatable.UpdatePlayAppStoreReleaseDefaults, state),
    );

    const releaseSettingsUpdateError = useSelector((state: RootState) =>
        getError(AppSettingsUpdatable.UpdatePlayAppStoreReleaseDefaults, state),
    );

    const onSaveChanges = (stagedRelease: boolean, initialStagedRolloutPercentage?: number) => {
        const request: UpdatePlayAppStoreReleaseDefaults = {
            orgId: props.orgId,
            appId: props.appId,
            stagedRelease,
            initialStagedRolloutPercentage,
        };

        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdatePlayAppStoreReleaseDefaults, request));
    };

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('Play Console release defaults updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    return (
        <EditGoogleReleaseSettings
            open={props.open}
            isConfiguringAppDefaults={true}
            isLoading={releaseSettingsUpdateLoading}
            customStagedRolloutEnabled={customStagedRolloutEnabled}
            customStagedRolloutDaysToPercent={customStagedRolloutDaysToPercent}
            googleAppStoreSettings={appStoreDefaults}
            onSubmit={onSaveChanges}
            onCancel={props.onClose}
        />
    );
};

export default EditGoogleAppReleaseDefaults;
